
/**
 * Module dependencies.
 */

import { ProductProps } from 'src/types/products';
import {
  fetchProductsCollection,
  paginationLimit
} from './fetch-products-collection';

import { useInfiniteQuery } from 'react-query';
import { useRouter } from 'next/router';

/**
 * Export `InitialData` type.
 */

export type InitialData = {
  pageParams: number[],
  pages: Array<{
    data: ProductProps[],
    nextPage: number | null,
    total: number
  }>
};

/**
 * `Options` type.
 */

type Options = {
  collectionId: string,
  filterParams: any,
  initialData?: InitialData | null
};

/**
 * Action type.
 */

const actionType = ({ collectionId, locale }): string => {
  return `${locale}-products-collection-${collectionId}`;
};

/**
 * `useProductsCollection` hook.
 */

function useProductsCollection(options: Options) {
  const { collectionId, filterParams, initialData } = options;
  const { locale } = useRouter();
  const result = useInfiniteQuery(
    actionType({ collectionId, locale }),
    ({ pageParam = 1 }) => fetchProductsCollection({
      collectionId,
      locale,
      params: {
        filter: filterParams,
        limit: paginationLimit,
        page: pageParam
      }
    }), {
      getNextPageParam: (lastPage, allPages) => {
        const pageLimit = Math.ceil(allPages[0]?.total / paginationLimit);
        const nextPage = lastPage?.nextPage;

        return nextPage <= pageLimit ? nextPage : false;
      },
      initialData
    }
  );

  return result;
}

/**
 * Export `useProductsCollection` hook.
 */

export default useProductsCollection;
